<!--
// app/pages/[region]/stats/index.vue.vue
-->
<script setup lang="ts">
import type { SeasonStatsPartner } from "~/src/season-cluster/domain/models/01.Season";
import HeaderBannerTitle from "~/app/components/Shared/HeaderBannerTitle.vue";
import GridFeaturedStatsPlayersOverview from "~/app/components/pages/region-stats/GridFeaturedStatsPlayersOverview.vue";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import SidebarMenuStats from "~/app/components/pages/region-stats/SidebarMenuStats.vue";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useWebContext } from "~/app/composables/webContext";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { useRegions } from "~/app/composables/regions";
import { $SEASONS_WITHOUT_DATA } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";
import ButtonSelectTypeStats from "~/app/components/pages/region-stats/ButtonSelectTypeStats.vue";
import { useSeasons } from "~/app/composables/season/seasons";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/stats",
    es: "/[region]/estadisticas",
    de: "/[region]/statistiken",
    fr: "/[region]/statistiques",
    it: "/[region]/statistiche",
    pt: "/[region]/estatisticas",
    ar: "/[region]/stats",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
});

const { t } = useI18n();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { isWorldCup, isSpain } = useRegions();
const { webContextGenders, contextSiteTitle, isQueens } = useWebContext();
const { currentSeason, region } = useCompetitionStore();
const { getSeason } = useSeasons();
const gridStatsKey = ref<number>(0);
const sidebarKey = ref<number>(0);
const loadingSeasonData = ref<boolean>(false);
const errorMessage = ref<boolean>(false);

const seasonMvpPartner = ref<SeasonStatsPartner>();
const bestGkId = ref<string | number | undefined>();

const getSeasonData = async (): Promise<void> => {
  try {
    loadingSeasonData.value = true;
    errorMessage.value = false;

    const seasonIdValue = seasonId.value;

    seasonMvpPartner.value =
      currentSeason && seasonIdValue === currentSeason.id
        ? currentSeason.metaInformation?.stats_partner
        : undefined;

    if (currentSeason && seasonIdValue === currentSeason.id) {
      bestGkId.value =
        currentSeason && currentSeason.metaInformation
          ? currentSeason.metaInformation.best_goalkeeper_id
          : undefined;
    } else {
      const season = await getSeason(seasonIdValue);
      bestGkId.value =
        season && season.metaInformation ? season.metaInformation.best_goalkeeper_id : undefined;
    }
  } catch (e) {
    errorMessage.value = true;
  } finally {
    loadingSeasonData.value = false;
    gridStatsKey.value++;
  }
};

await nextTick();
await getSeasonData();

useSeoMeta({
  title: t("menuItem.stats") + " " + t("menuItem.statsPlayers"),
  ogTitle: t("menuItem.stats") + " " + t("menuItem.statsPlayers"),
  description: t("meta.description.stats.page.players", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.stats.page.players", { league: region.leagueTitle || contextSiteTitle }),
});
</script>

<template>
  <header-banner-title
    :title="$t('general.stats.title') + ' ' + $t('menuItem.statsPlayers')"
  ></header-banner-title>
  <section class="px-4 md:px-6">
    <div
      class="kql-w-container flex flex-col md:flex-row items-stretch sm:items-end justify-center md:items-center md:justify-between flex-wrap"
    >
      <button-select-type-stats
        v-if="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
        data-type="players"
        :page="undefined"
      ></button-select-type-stats>
      <div v-else></div>
      <seasons-filter
        v-model:seasonClusterId="seasonClusterId"
        v-model:seasonId="seasonId"
        @change="getSeasonData"
        combined-with-more-filters
      />
    </div>
  </section>
  <section class="px-4 md:px-6 py-9">
    <div class="kql-w-container grid grid-cols-1 lg:grid-cols-4 py-9 px-4 bg-gray-900 rounded-lg lg:gap-4">
      <div class="mb-9 lg:mb-0">
        <sidebar-menu-stats
          :new-stats="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
          :key="sidebarKey"
          :season-id="seasonId"
        ></sidebar-menu-stats>
      </div>
      <div class="col-span-3">
        <div v-if="!errorMessage">
          <grid-featured-stats-players-overview
            :key="gridStatsKey"
            :loading="loadingSeasonData"
            :partner="seasonMvpPartner"
            :season-id="seasonId"
            :best-gk-id="bestGkId"
          ></grid-featured-stats-players-overview>
        </div>
        <div v-else>{{ $t("stats.message.notAvailable") }}</div>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
